// Story 8.5 — the creator payout panel: the earnings pot, the two things a
// creator can do with it, where we pay them, and their invoice archive.
//
// ---------------------------------------------------------------------------
// THE TWO ACTIONS ARE SHOWN WITH EQUAL PROMINENCE, AND THAT IS AN AC
// ---------------------------------------------------------------------------
//
// "Request a payout" and "Move to my balance" sit side by side, same size, same
// weight. Epic 8's Story 8.5 AC is explicit: the free earnings → balance
// conversion is offered *alongside payout with equal prominence* — including,
// and especially, to creators whose country the payout provider does not cover,
// for whom it is the only route. Demoting it to a text link would turn "here
// are your two options" into "here is the option, and a footnote", which is
// exactly the wrong shape for the creator this rail cannot reach.
//
// ---------------------------------------------------------------------------
// WHAT THIS PANEL DOES NOT SHOW
// ---------------------------------------------------------------------------
//
// The Greenhold balance. Two pots, never summed and never side-by-side-summable
// (Story 8.2 AC1): this surface is about the pot that can be paid out, and the
// wallet page is where both are shown together, separately labelled. The word
// list (UX-DR49) applies throughout: "earnings", "revenue share", "paid out"
// belong to this pot; "withdraw", "cash out" and "your money" belong to nothing.

import { useState } from "react";
import { Badge, Button, Card, CardContent, Skeleton } from "@greenhold/components";

import { ConvertEarningsModal } from "../earnings/ConvertEarningsModal";
import { CreatorIdentitySection } from "./CreatorIdentitySection";
import { OnboardingPanel } from "./OnboardingPanel";
import { RequestPayoutModal } from "./RequestPayoutModal";
import {
  usePayoutOptions,
  type CreatorPayoutIneligibleReason,
} from "./usePayoutOptions";
import { useSettlements, type SettlementStatus } from "./useSettlements";

function formatEur(cents: number): string {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents);
  return `${sign}€${Math.floor(abs / 100)}.${(abs % 100)
    .toString()
    .padStart(2, "0")}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/// Every reason is phrased so the creator knows what to do next, and none of
/// them is a dead end — the free conversion is always there.
const REASON_COPY: Record<CreatorPayoutIneligibleReason, string> = {
  country_unsupported:
    "Our payout provider doesn't reach your country yet. You can still move " +
    "your earnings to your Greenhold balance, free — and we'll tell you the " +
    "moment payouts open where you are.",
  country_unknown:
    "We can't confirm payouts to your country yet. Moving your earnings to " +
    "your Greenhold balance is free and available now.",
  rail_unsupported:
    "That payout method isn't available from our provider. Bank transfer is.",
  below_minimum:
    "You're below the minimum for one settlement. Your earnings keep " +
    "accruing to the next period — or move them to your Greenhold balance " +
    "now, free, with no minimum.",
  already_queued_this_period:
    "You already have a payout invoiced for this period. It's paid at the " +
    "cut-off; the next one opens straight after.",
  // Story 8.10. Points at the section BELOW, which is the remedy — not at the
  // creator application, which an approved creator cannot edit. Without this
  // entry the panel rendered an empty <p>: the server sent a reason the SPA's
  // copy map had never heard of.
  identity_not_on_file:
    "We need your legal name and address before we can raise a settlement " +
    "document for you — a payment record has to name who it is with. Add them " +
    "under \"Your details\" below. Your earnings keep accruing, and moving them " +
    "to your Greenhold balance is free and available now.",
};

const STATUS_COPY: Record<SettlementStatus, { label: string; hint: string }> = {
  queued: {
    label: "Invoiced",
    hint: "Paid at the settlement cut-off.",
  },
  initiated: {
    label: "Sending",
    hint: "We've asked our provider to send it.",
  },
  paid: {
    label: "On its way",
    hint: "Your bank or card network decides the final timing.",
  },
  compensated: {
    label: "Returned",
    hint: "We couldn't deliver it, so the full amount went back to your earnings.",
  },
  failed: {
    label: "Needs attention",
    hint: "Something went wrong that we have to fix by hand — we're on it.",
  },
};

export function CreatorPayoutPanel() {
  const options = usePayoutOptions();
  const settlements = useSettlements();
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  if (options.isPending) {
    return <Skeleton className="h-64 w-full rounded-lg" />;
  }

  if (options.isError || !options.data) {
    return (
      <Card>
        <CardContent className="p-6 space-y-3">
          <p className="text-destructive">
            Couldn't load your payout settings.
          </p>
          <Button variant="secondary" onClick={() => options.refetch()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const data = options.data;
  const needsDestination = data.destination === null;
  const destinationPending =
    data.destination !== null && data.destination.status !== "verified";

  return (
    <div className="space-y-6">
      {/* The pot, and the two things you can do with it. */}
      <Card>
        <CardContent className="p-6 space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wider font-mono opacity-60">
              Your earnings
            </p>
            <p className="text-3xl font-heading font-bold">
              {formatEur(data.earnings_cents)}
            </p>
            <p className="text-sm opacity-70 mt-1">
              Revenue share you've earned. This is the only part of Greenhold
              that pays out — your Greenhold balance is for spending here.
            </p>
          </div>

          {/* EQUAL PROMINENCE. Same variant, same size, same row. */}
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => setPayoutOpen(true)}
              disabled={!data.can_request}
            >
              Request a payout
            </Button>
            <Button
              onClick={() => setConvertOpen(true)}
              disabled={data.earnings_cents <= 0}
            >
              Move to my Greenhold balance — free
            </Button>
          </div>

          {/* `?? null` rather than a bare lookup: the server owns this
              vocabulary, and a reason shipped ahead of its SPA copy must not
              render an empty paragraph where an explanation belongs. Story 8.10
              added `identity_not_on_file` and this is how that gap showed up. */}
          {data.ineligible_reason ? (
            <p className="text-sm opacity-80">
              {REASON_COPY[data.ineligible_reason] ??
                "We can't settle a payout right now. Moving your earnings to " +
                  "your Greenhold balance is free and available."}
            </p>
          ) : null}

          {!data.ineligible_reason && needsDestination ? (
            <p className="text-sm opacity-80">
              Tell us where to pay you and we can settle your revenue share.
            </p>
          ) : null}
          {destinationPending ? (
            <p className="text-sm opacity-80">
              We're verifying your payout details. Payouts open as soon as
              that's done.
            </p>
          ) : null}

          <div className="text-xs opacity-60 border-t pt-3 space-y-1">
            {/* The creator terms (§8.6) point HERE for the schedule rather
                than pinning it in the contract, so the period, the cut-off and
                the minimum have to be legible on this page — otherwise the ToS
                points at nothing. */}
            <p>
              We settle on a schedule. The current period ends{" "}
              {formatDate(data.period.end)}, and invoices raised in it are paid
              then. Minimum per settlement:{" "}
              {formatEur(data.minimum_cents)} — the free move to your Greenhold
              balance has no minimum.
            </p>
            {data.rails.length > 0 ? (
              <p>
                Fee:{" "}
                {data.rails
                  .map(
                    (r) =>
                      `${r.rail === "bank" ? "bank transfer" : "to your card"} ${formatEur(
                        r.fee_cents,
                      )}`,
                  )
                  .join(" · ")}
                . Flat, equal to what the payment costs us. Moving earnings to
                your Greenhold balance is always free.
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* Story 8.10 — WHO we pay, which is a different question from WHERE.
          Shown ALWAYS for an approved creator, not only when something is
          missing: Greenhold holds this person's legal name and home address and
          prints them on a document in its own books, so being able to check and
          correct them is not a repair flow, it is the normal case. `null` means
          the caller holds no approved application at all, and this section does
          not apply to them. */}
      {data.counterparty_identity ? (
        <CreatorIdentitySection
          identity={data.counterparty_identity}
          onFile={data.counterparty_identity_on_file}
        />
      ) : null}

      {/* Where we pay you — Story 1.17's IBAN form, creator-scoped. */}
      <div>
        <h3 className="text-lg font-heading font-semibold mb-2">
          Where we pay you
        </h3>
        <OnboardingPanel />
      </div>

      {/* The invoice archive. */}
      <div>
        <h3 className="text-lg font-heading font-semibold mb-2">
          Your revenue-share invoices
        </h3>
        {settlements.isPending ? (
          <Skeleton className="h-24 w-full rounded-lg" />
        ) : settlements.data && settlements.data.length > 0 ? (
          <div className="space-y-3">
            {settlements.data.map((s) => (
              <Card key={s.id}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-mono text-sm">{s.invoice_number}</p>
                      <p className="text-xs opacity-70">
                        {formatDate(s.period_start)} – {formatDate(s.period_end)}
                      </p>
                    </div>
                    <Badge variant="secondary">
                      {STATUS_COPY[s.status].label}
                    </Badge>
                  </div>
                  <dl className="text-sm space-y-1">
                    {s.lines.map((line) => (
                      <div key={line.line_kind} className="flex justify-between">
                        <dt className="opacity-70">
                          {line.line_kind === "creator_royalty"
                            ? "Resale royalties"
                            : "Revenue share on dossier sales"}
                        </dt>
                        <dd>{formatEur(line.amount_cents)}</dd>
                      </div>
                    ))}
                    <div className="flex justify-between">
                      <dt className="opacity-70">Payout fee</dt>
                      <dd>-{formatEur(s.fee_cents)}</dd>
                    </div>
                    <div className="flex justify-between font-semibold">
                      <dt>Net to you</dt>
                      <dd>{formatEur(s.net_cents)}</dd>
                    </div>
                  </dl>
                  <p className="text-xs opacity-60">
                    {STATUS_COPY[s.status].hint}
                  </p>
                  {/* No VAT/tax disclosure here, deliberately (owner ruling
                      2026-09-04, docs/creator-share-net-proceeds-2026-09-04.md
                      §1.2 — momsloven § 52 a, stk. 6-7 stated-VAT trap). F13. */}
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="p-6 text-sm opacity-75">
              No invoices yet. When you request a payout, we raise one for you
              here — with the period, what you earned, the fee and the net.
            </CardContent>
          </Card>
        )}
      </div>

      <RequestPayoutModal
        open={payoutOpen}
        onOpenChange={setPayoutOpen}
        options={data}
      />
      <ConvertEarningsModal
        open={convertOpen}
        onOpenChange={setConvertOpen}
        earningsCents={data.earnings_cents}
      />
    </div>
  );
}
